import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { bankApi, type BankApiBank, type BankApiConnection, type BankApiFetchLog } from '@/api/bankApi';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import Drawer from '@/components/Drawer';

/**
 * ч.739: Банк → Підключення до банку (API). Приват24 «Автоклієнт» (token з
 * кабінету) і ПУМБ ІКБ (client_id/secret з settings.txt банку + логін/пароль).
 * ч.929: виписка тягнеться за РОЗКЛАДОМ (слоти робочого дня + «оновити при
 * відкритті» сторінок банку + «синхронізувати зараз»), не «кожні 15 хв» —
 * при тисячах клієнтів банки блокують. ч.1305 (рішення власника): підключення
 * ЗАВЖДИ створює документи з операцій; у «налаштуваннях» лише дві галочки —
 * «розпізнавати автоматично» (нерозпізнане → «нерозпізнані» у списках) і
 * «проводити розпізнані»; пауза — «вимкнути» у рядку. Збережені списання
 * відправляються у банк з /bank-payments.
 */
const BANK_LABEL: Record<BankApiBank, string> = {
  privat: 'ПриватБанк (Приват24 Автоклієнт)',
  pumb: 'ПУМБ (ІКБ REST API)',
  mono: 'Монобанк — ФОП (особистий токен)',
  monocorp: 'Монобанк — юрособи (токен компанії)',
};
/**
 * ч.1131 (рішення власника): що пропонуємо у формі. Рахунки ФОП у Монобанку
 * (особистий токен, вид `mono`) поки НЕ підключаємо — банк вимагає від сервісів
 * іншого способу доступу; сервер такий запит теж відхиляє. Підпис у BANK_LABEL
 * лишається, щоб наявний рядок (якщо зʼявиться) мав назву.
 */
export const OFFERED_BANKS: BankApiBank[] = ['privat', 'pumb', 'monocorp'];
/** Банки, де для підключення потрібен лише токен. */
const TOKEN_BANKS: BankApiBank[] = ['privat', 'mono', 'monocorp'];
/** ч.1130: де взяти токен і що підключення вміє — видно в формі, не лише в підказці. */
export const TOKEN_HINT: Partial<Record<BankApiBank, { label: string; hint: string }>> = {
  privat: {
    label: 'Token Автоклієнта',
    hint: 'Приват24 для бізнесу → Меню → Інтеграція (Автоклієнт) → Підключити API → скопіюйте токен.',
  },
  monocorp: {
    label: 'Токен компанії',
    hint: 'Веб-кабінет web.monobank.ua → оберіть компанію → розділ токенів. Виписки підтягуються '
      + 'автоматично, збережені списання надсилаються в банк на підпис. Рахунки ФОП у Монобанку '
      + 'поки не підключаються.',
  },
};
const fmtDt = (v: string | null) => v ? new Date(v).toLocaleString('uk-UA') : '—';
const fmtShort = (v: string | null) => v
  ? new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

interface SettingsForm { auto_recognize: boolean; auto_post_recognized: boolean; fetch_days_back: number }

export default function BankApiConnectionsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [orgId, setOrgId] = useOrgFilter();
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const listQ = useQuery({
    queryKey: ['bank-api-connections', orgId],
    queryFn: () => bankApi.list(orgId), enabled: !!orgId,
  });
  const [note, setNote] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['bank-api-connections'] });
  // ч.1343: причину від сервера дописуємо до «Не вдалося …» — інакше повідомлення
  // про помилку («Днів назад: має бути не більше 90») світилось зеленим, як успіх.
  const errText = (e: unknown, fb: string) => {
    const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof d === 'string' && d ? `${fb.replace(/\.$/, '')}: ${d}` : fb;
  };

  // Форма нового підключення.
  const [adding, setAdding] = useState(false);
  const [bank, setBank] = useState<BankApiBank>('privat');
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [daysBack, setDaysBack] = useState(3);
  const reset = () => { setName(''); setToken(''); setClientId(''); setClientSecret(''); setUsername(''); setPassword(''); };

  const create = useMutation({
    mutationFn: () => bankApi.create({
      organization_id: orgId, bank, name: name.trim() || BANK_LABEL[bank],
      credentials: TOKEN_BANKS.includes(bank) ? { token: token.trim() }
        : { client_id: clientId.trim(), client_secret: clientSecret.trim(), username: username.trim(), password },
      auto_recognize: true, auto_post_recognized: true, fetch_days_back: daysBack,
    }),
    onSuccess: () => { setAdding(false); reset(); setNote('Підключення збережено. Натисніть «перевірити».'); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося зберегти підключення.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => bankApi.remove(id),
    onSuccess: () => invalidate(),
  });
  const test = useMutation({
    mutationFn: (id: string) => bankApi.test(id),
    onSuccess: (r) => {
      // ч.1130: Монобанк показує всі рахунки, які бачить токен; виписка йде лише
      // за тими, що заведені в організації, — решту позначаємо.
      const accs = r.accounts.map((a) => `${a.iban ?? ''}${a.balance != null ? ` (${a.balance})` : ''}`
        + (a.in_organization === false ? ' — не заведено в організації' : '')).join(', ');
      setNote(r.ok ? `${r.message}${accs ? `. Рахунки: ${accs}` : ''}` : `Не вдалося: ${r.message}`);
      invalidate();
    },
    onError: (e) => setNote(errText(e, 'Не вдалося перевірити.')),
  });
  const navigate = useNavigate();
  // ч.929: «синхронізувати зараз» — той самий прохід, що й за розкладом
  // (з урахуванням галок налаштувань), через темп на банк.
  const syncNow = useMutation({
    mutationFn: (id: string) => bankApi.sync(id),
    onSuccess: (r) => {
      setNote(r.error
        ? `Синхронізація з помилкою: ${r.error}`
        : `Синхронізовано: отримано ${r.rows_fetched}, нових ${r.rows_new}, створено ${r.rows_created}, проведено ${r.rows_posted}, на перевірку ${r.rows_review}.`);
      invalidate();
      qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith('bank-') });
    },
    onError: (e) => setNote(errText(e, 'Не вдалося синхронізувати.')),
  });
  // Шухляда налаштувань підключення (галки + вікно першої синхронізації).
  const [settingsFor, setSettingsFor] = useState<BankApiConnection | null>(null);
  const [sf, setSf] = useState<SettingsForm>({ auto_recognize: true, auto_post_recognized: true, fetch_days_back: 3 });
  useEffect(() => {
    if (settingsFor) setSf({
      auto_recognize: settingsFor.auto_recognize, auto_post_recognized: settingsFor.auto_post_recognized,
      fetch_days_back: settingsFor.fetch_days_back,
    });
  }, [settingsFor]);
  const saveSettings = useMutation({
    mutationFn: () => bankApi.update(settingsFor!.id, {
      auto_recognize: sf.auto_recognize, auto_post_recognized: sf.auto_post_recognized, fetch_days_back: sf.fetch_days_back,
    }),
    onSuccess: () => { setSettingsFor(null); setNote('Налаштування збережено.'); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося зберегти налаштування.')),
  });
  // ч.1305: пауза підключення — розклад і «оновити при відкритті» його пропускають.
  const toggleEnabled = useMutation({
    mutationFn: (c: BankApiConnection) => bankApi.update(c.id, { enabled: !c.enabled }),
    onSuccess: (c) => { setNote(c.enabled ? 'Підключення увімкнено.' : 'Підключення призупинено — виписка за розкладом не тягнеться.'); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося змінити стан підключення.')),
  });
  const [logFor, setLogFor] = useState<string | null>(null);
  const logQ = useQuery({
    queryKey: ['bank-api-log', logFor], queryFn: () => bankApi.log(logFor!), enabled: !!logFor,
  });

  const cols = useMemo<ColumnDef<BankApiConnection>[]>(() => [
    { key: 'bank', header: 'Банк', width: 220, sortValue: (r) => r.bank, cell: (r) => BANK_LABEL[r.bank] },
    { key: 'name', header: 'Назва', sortValue: (r) => r.name, cell: (r) => r.name },
    {
      key: 'creds', header: 'Реквізити', width: 220,
      cell: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {Object.entries(r.credentials_masked).map(([k, v]) => `${k}: ${v}`).join(' · ')}</span>,
    },
    {
      key: 'last', header: 'Остання синхронізація', width: 250,
      sortValue: (r) => r.last_fetch_at ?? '',
      cell: (r) => (
        <span title={r.last_error ?? undefined} style={{ display: 'inline-block', lineHeight: 1.25 }}>
          <span style={{ color: r.last_fetch_status === 'error' ? '#b91c1c' : r.last_fetch_status === 'partial' ? '#b45309' : undefined }}>
            {fmtDt(r.last_fetch_at)}{r.last_fetch_status ? ` · ${r.last_fetch_status}` : ''}
            {r.fail_count > 1 ? ` (помилок поспіль: ${r.fail_count})` : ''}
          </span>
          <br />
          <small style={{ color: r.enabled ? 'var(--muted)' : 'var(--badge-amber-fg)' }}>
            {r.enabled
              ? `наступна: ${fmtShort(r.next_fetch_at)} · ${r.auto_recognize ? (r.auto_post_recognized ? 'розпізнавати і проводити' : 'розпізнавати, не проводити') : 'усе у «нерозпізнані»'}`
              : 'призупинено'}
          </small>
        </span>
      ),
    },
    {
      key: 'actions', header: '', width: 520, align: 'center',
      cell: (r) => (
        <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', gap: 4 }}>
          <button type="button" disabled={test.isPending} onClick={() => test.mutate(r.id)}>перевірити</button>
          <button type="button" className="primary"
            title="Отримати операції з банку за період і розібрати їх так само, як файл виписки (Банк → Імпорт виписки)"
            onClick={() => navigate(`/bank-import?source=api&org=${r.organization_id}`)}>отримати виписку</button>
          <button type="button" disabled={syncNow.isPending}
            title="Підтягнути нові операції зараз, не чекаючи розкладу (за налаштуваннями підключення)"
            onClick={() => syncNow.mutate(r.id)}>синхронізувати зараз</button>
          <button type="button" title="Розпізнавати автоматично, проводити розпізнані, вікно першої синхронізації"
            onClick={() => setSettingsFor(r)}>налаштування</button>
          <button type="button" disabled={toggleEnabled.isPending}
            title={r.enabled ? 'Призупинити: виписка за розкладом не тягнеться, документи не створюються' : 'Увімкнути: виписка знову тягнеться за розкладом'}
            onClick={() => toggleEnabled.mutate(r)}>{r.enabled ? 'вимкнути' : 'увімкнути'}</button>
          <button type="button" onClick={() => setLogFor(logFor === r.id ? null : r.id)}>журнал</button>
          <button type="button" className="danger" title="Видалити підключення"
            onClick={() => confirm(`Видалити підключення «${r.name}»?`).then((ok) => { if (ok) remove.mutate(r.id); })}>✕</button>
        </span>
      ),
    },
  ], [test, remove, confirm, logFor, navigate, syncNow.isPending, toggleEnabled]);

  const logCols = useMemo<ColumnDef<BankApiFetchLog>[]>(() => [
    { key: 'started_at', header: 'Початок', width: 160, sortValue: (r) => r.started_at, cell: (r) => fmtDt(r.started_at) },
    { key: 'trigger', header: 'Хто', width: 90, cell: (r) => r.trigger === 'manual' ? 'вручну' : r.trigger === 'open' ? 'відкриття' : 'розклад' },
    { key: 'period', header: 'Період', width: 190, cell: (r) => `${r.date_from ?? ''} — ${r.date_to ?? ''}` },
    { key: 'rows_fetched', header: 'Отримано', width: 90, align: 'right', cell: (r) => r.rows_fetched },
    { key: 'rows_new', header: 'Нових', width: 80, align: 'right', cell: (r) => r.rows_new },
    { key: 'rows_created', header: 'Створено', width: 90, align: 'right', cell: (r) => r.rows_created },
    { key: 'rows_posted', header: 'Проведено', width: 90, align: 'right', cell: (r) => r.rows_posted },
    { key: 'rows_confirmed', header: 'Підтв. чернеток', width: 120, align: 'right', cell: (r) => r.rows_confirmed },
    { key: 'rows_review', header: 'На перевірку', width: 110, align: 'right', cell: (r) => r.rows_review },
    { key: 'error', header: 'Помилка', cell: (r) => <span style={{ color: 'var(--badge-red-fg)' }}>{r.error ?? ''}</span> },
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Підключення до банку (API)</h2>
        <select value={orgId} style={{ width: 220 }} onChange={(e) => setOrgId(e.target.value)}>
          <option value="">— організація —</option>
          {(orgListQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button className="primary" style={{ marginLeft: 'auto' }} disabled={!orgId}
          onClick={() => { setAdding(true); setNote(null); }}>+ додати підключення</button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 900, marginTop: 0 }}>
        Виписка підтягується за розкладом кілька разів на робочий день (вранці, вдень, надвечір), при відкритті
        сторінок «Списання»/«Надходження» і кнопкою «синхронізувати зараз». Розпізнані операції (контрагент за
        ЄДРПОУ/IBAN або правилом) створюються і проводяться, нерозпізнані — чернеткою з позначкою «перевірити»;
        що саме робити автоматично — у «налаштуваннях» підключення. Ваші збережені списання підтверджуються
        автоматично, а відправити їх у банк на підпис можна з «Банк → Списання → збережені». Підписує платежі
        уповноважена особа в кабінеті банку — система ключів не має.
      </p>
      {note && (
        <div style={{ margin: '0.5rem 0', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem',
          background: note.startsWith('Не вдалося') || note.startsWith('Синхронізація з помилкою') ? '#fee2e2' : '#dcfce7',
          border: `1px solid ${note.startsWith('Не вдалося') || note.startsWith('Синхронізація з помилкою') ? '#fca5a5' : '#86efac'}` }}>
          {note}<button type="button" style={{ marginLeft: 8, padding: '0 0.4rem' }} onClick={() => setNote(null)}>×</button>
        </div>
      )}

      {adding && (
        <div style={{ padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: 6, marginBottom: '0.75rem' }}>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Банк</label>
              <select value={bank} onChange={(e) => setBank(e.target.value as BankApiBank)}>
                {OFFERED_BANKS.map((b) => <option key={b} value={b}>{BANK_LABEL[b]}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Назва</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={BANK_LABEL[bank]} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label title="Скільки днів назад дотягувати при кожній синхронізації (перекриття)">Днів назад</label>
              <input type="number" min={1} max={90} value={daysBack} onChange={(e) => setDaysBack(Number(e.target.value) || 3)} />
            </div>
          </div>
          {TOKEN_BANKS.includes(bank) ? (
            <div className="field">
              <label htmlFor="bank-api-token">{TOKEN_HINT[bank]?.label ?? 'Токен'}</label>
              <input id="bank-api-token" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.25rem', maxWidth: 900 }}>
                {TOKEN_HINT[bank]?.hint}
              </small>
            </div>
          ) : (
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 3' }}><label>client_id (settings.txt)</label>
                <input value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" /></div>
              <div className="field" style={{ gridColumn: 'span 3' }}><label>client_secret (settings.txt)</label>
                <input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} type="password" autoComplete="new-password" /></div>
              <div className="field" style={{ gridColumn: 'span 3' }}><label>Логін ІКБ</label>
                <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" /></div>
              <div className="field" style={{ gridColumn: 'span 3' }}><label>Пароль ІКБ</label>
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password" /></div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.4rem' }}>
            <small style={{ color: 'var(--muted)' }}>
              Операції з банку створюються документами; розпізнавання й автопроведення увімкнені — змінити можна у
              «налаштуваннях» після збереження.
            </small>
            <button className="primary" style={{ marginLeft: 'auto' }} disabled={create.isPending}
              onClick={() => create.mutate()}>зберегти</button>
            <button type="button" onClick={() => { setAdding(false); reset(); }}>скасувати</button>
          </div>
        </div>
      )}

      <DataTable<BankApiConnection> rows={listQ.data ?? []} columns={cols} rowKey={(r) => r.id}
        loading={!!orgId && listQ.isLoading}
        emptyText={orgId ? 'Підключень немає — додайте ПриватБанк, ПУМБ або Монобанк.' : 'Оберіть організацію.'} />

      {settingsFor && (
        <Drawer open title={`Налаштування — ${settingsFor.name}`} onClose={() => setSettingsFor(null)}
          footer={
            <>
              <button type="button" onClick={() => setSettingsFor(null)}>скасувати</button>
              <button type="button" className="primary" disabled={saveSettings.isPending}
                onClick={() => saveSettings.mutate()}>зберегти</button>
            </>
          }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {/* ч.1305 (рішення власника): дві галочки. Документи з операцій створюються завжди. */}
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input type="checkbox" checked={sf.auto_recognize} style={{ width: 'auto', marginTop: 3 }}
                onChange={(e) => setSf({ ...sf, auto_recognize: e.target.checked })} />
              <span><b>Розпізнавати автоматично</b><br />
                <small style={{ color: 'var(--muted)' }}>
                  Для кожної операції шукати контрагента (ЄДРПОУ, IBAN, правила), вид операції і підставу.
                  Розпізнане стає звичайним документом; що не розпізналось — у вкладку «нерозпізнані» надходжень
                  чи списань. Вимкнено — усі операції потрапляють у «нерозпізнані», і ви розбираєте їх самі.
                </small></span>
            </label>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', opacity: sf.auto_recognize ? 1 : 0.55 }}>
              <input type="checkbox" checked={sf.auto_post_recognized} disabled={!sf.auto_recognize}
                style={{ width: 'auto', marginTop: 3 }}
                onChange={(e) => setSf({ ...sf, auto_post_recognized: e.target.checked })} />
              <span><b>Проводити розпізнані</b><br />
                <small style={{ color: 'var(--muted)' }}>
                  Розпізнані операції одразу проводити — і під час синхронізації, і як основна дія на сторінці
                  «отримати виписку». Вимкнено — документи створюються чернетками, проводите самі.
                </small></span>
            </label>
            <label>Днів назад при першій синхронізації
              <input type="number" min={1} max={90} value={sf.fetch_days_back} style={{ width: 120 }}
                onChange={(e) => setSf({ ...sf, fetch_days_back: Number(e.target.value) || 3 })} />
              <small style={{ color: 'var(--muted)', display: 'block' }}>
                Далі береться період від останньої вдалої синхронізації з перекриттям в один день.
              </small>
            </label>
            <small style={{ color: 'var(--muted)' }}>
              Наступна синхронізація за розкладом: {fmtDt(settingsFor.next_fetch_at)}.
              {settingsFor.last_ok_at ? ` Остання вдала: ${fmtDt(settingsFor.last_ok_at)}.` : ''}
            </small>
          </div>
        </Drawer>
      )}

      {logFor && (
        <div style={{ marginTop: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.4rem' }}>Журнал синхронізацій</h3>
          <DataTable<BankApiFetchLog> rows={logQ.data ?? []} columns={logCols} rowKey={(r) => r.id}
            loading={logQ.isLoading} emptyText="Синхронізацій ще не було." initialSortKey="started_at" initialSortDir="desc" />
        </div>
      )}

    </div>
  );
}
